<template>
  <div v-if="isError" class="flex justify-center py-12">
    <p class="flex items-center gap-2 rounded-sm bg-bittersweet-600 px-2 text-white">
      <CircleAlertIcon class="size-4" /> {{ errorMessage }}
    </p>
  </div>
  <div v-else-if="media" class="space-y-4">
    <div class="flex items-center justify-between pt-2">
      <div class="flex items-center gap-3">
        <h2>{{ media.title }}</h2>
        <MediaEncodingStatusBadge :status="media.encodingStatus" size="md" />
      </div>
      <div class="flex items-center gap-4">
        <CVButton
          v-if="$can('delete', 'Media')"
          variant="danger"
          size="iconSm"
          @click="emit('delete')"
          ><TrashIcon class="h-4 w-4"
        /></CVButton>
        <CVButton
          v-if="$can('update', 'Media')"
          variant="secondary"
          size="iconSm"
          @click="emit('edit')"
          ><PencilIcon class="h-4 w-4"
        /></CVButton>
      </div>
    </div>

    <div class="aspect-video w-full overflow-hidden rounded-lg bg-black">
      <video
        v-if="media.type === 'VIDEO' && media.fileUrl && media.encodingStatus === 'READY'"
        :key="media.fileUrl"
        ref="playerRef"
        class="h-full w-full"
        controls
        playsinline
        :poster="media.posterFileUrl ?? undefined"
      />
      <div v-else class="flex h-full w-full items-center justify-center text-white">
        {{ t('videoNotReady') }}
      </div>
    </div>
  </div>
  <div v-else class="flex items-center justify-center py-12">
    <Spinner class="text-hazel-600" />
  </div>
</template>

<script lang="ts" setup>
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { useMedia } from '@/queries/use-media';
import MediaEncodingStatusBadge from '@/components/badges/MediaEncodingStatusBadge.vue';
import { CircleAlertIcon, PencilIcon, TrashIcon } from 'lucide-vue-next';
import type Hls from 'hls.js';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isAxiosError } from 'axios';

const props = defineProps<{
  mediaId: string;
}>();

const emit = defineEmits<{
  (e: 'edit'): void;
  (e: 'delete'): void;
}>();

const { t } = useI18n();
const { data: media, isError, error } = useMedia(() => props.mediaId);

const errorMessage = computed(() => {
  const err = error.value;
  if (!err) return t('loadError');
  if (isAxiosError(err)) {
    const message = err.response?.data?.message;
    return typeof message === 'string' ? message : err.message;
  }
  return err instanceof Error ? err.message : String(err);
});
const playerRef = ref<HTMLVideoElement | null>(null);
let hlsInstance: Hls | null = null;

const destroyPlayer = () => {
  hlsInstance?.destroy();
  hlsInstance = null;
};

const setupPlayer = async () => {
  destroyPlayer();
  if (!media.value?.fileUrl || media.value.encodingStatus !== 'READY' || !playerRef.value) {
    return;
  }

  const Hls = (await import('hls.js')).default;
  if (Hls.isSupported()) {
    hlsInstance = new Hls({ capLevelToPlayerSize: true });
    hlsInstance.loadSource(media.value.fileUrl);
    hlsInstance.attachMedia(playerRef.value);
  } else {
    playerRef.value.src = media.value.fileUrl;
  }
};

watch(
  () => [media.value?.fileUrl, media.value?.encodingStatus, playerRef.value] as const,
  () => {
    void setupPlayer();
  },
  { flush: 'post', immediate: true },
);
onBeforeUnmount(destroyPlayer);
</script>

<i18n lang="json">
{
  "en": {
    "videoNotReady": "Video is not ready yet.",
    "loadError": "Could not load media."
  },
  "de": {
    "videoNotReady": "Video ist noch nicht bereit.",
    "loadError": "Medium konnte nicht geladen werden."
  },
  "es": {
    "videoNotReady": "El vídeo aún no está listo.",
    "loadError": "No se pudo cargar el medio."
  }
}
</i18n>
